import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Stack,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useDebouncedValue } from "@mantine/hooks"
import {
  CheckCircleIcon,
  XIcon,
} from "@phosphor-icons/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import classes from "./wizard.module.css"
import { StepMark } from "./WizardSection"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { DisabledReason, FormChecklist, HelpButtonGroup, HelpIcon, matchesBusinessName, nextStepToast, useChecklistMissing, useHelpContext } from "@dehub/theme"
import { DeliveryApi, DeliveryRouteApi, PullSheetApi, StaffApi, VehicleApi } from "../../../apis"
import { IDeliveryNotePlus } from "../../../models/DeliveryNote"
import { IDistributionArea } from "../../../models/DistributionArea"
import { IAuth } from "../../../store/Auth"
import { message } from "../../../utils/message"
import { renderFullName } from "../../../utils/render-name"
import { useRouteMutations } from "../hooks"
import { translateRouteError } from "../route-errors"
import { useVehicleClassLabel } from "../vehicle-class"
import { AssignmentSection } from "./AssignmentSection"
import { DnSelectionSection } from "./DnSelectionSection"
import { RouteInfoSection } from "./RouteInfoSection"
import { StatsPanel } from "./StatsPanel"
import { StopSequenceSection } from "./StopSequenceSection"
import { totalRouteKm, WizardMap } from "./WizardMap"
import { plannedStartIso, todayInUlaanbaatar } from "./today"
import type { DnFilters, DnRow, RouteInfoForm } from "./types"

function makeRouteNumber(date: string | null): string {
  const d = date ?? todayInUlaanbaatar()
  const compact = d.replace(/-/g, "")
  return `RT-${compact}-???`
}

function priorityRank(p?: string | null): number {
  if (p === "VIP") return 4
  if (p === "URGENT") return 3
  if (p === "HIGH") return 2
  if (p === "NORMAL") return 1
  return 0
}

export function CreateRouteWizard() {
  const { t } = useTranslation(["delivery", "common"])
  const classLabel = useVehicleClassLabel()
  const navigate = useNavigate()
  const location = useLocation()
  // DNs handed over from the pending-deliveries list ("Шинэ маршрут үүсгэх").
  const preselectedDnIds = useMemo(
    () => (location.state as { preselectedDnIds?: string[] } | null)?.preselectedDnIds ?? null,
    [location.state],
  )
  const preselectAppliedRef = useRef(false)
  const { create, optimize, submitting, error } = useRouteMutations()

  const [dns, setDns] = useState<DnRow[]>([])
  const [dnsLoading, setDnsLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [orderedIds, setOrderedIds] = useState<string[]>([])
  const [filters, setFilters] = useState<DnFilters>({
    zone: null, channel: "LOCAL_DELIVERY", vipOnly: false, slaRisk: false, search: "",
  })
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ value: string; label: string }>>([])
  const [warehouseById, setWarehouseById] = useState<Map<string, { lat: number; lng: number; name: string }>>(new Map())
  const [warehouseAccess, setWarehouseAccess] = useState<{ canChangeAny: boolean; warehouseIds: string[] } | null>(null)
  // Set once the operator picks a start warehouse by hand; until then it follows the deliveries.
  const warehouseTouchedRef = useRef(false)

  const form = useForm<RouteInfoForm>({
    initialValues: {
      name: "",
      zone: [],
      warehouseId: null,
      startTime: "",
      notes: "",
      plannedDate: todayInUlaanbaatar(),
    },
    validate: {
      plannedDate: (v) => (v ? null : t("delivery:route.wizard.dateRequired")),
      name: (v) => (v?.trim() ? null : t("delivery:route.wizard.nameRequired")),
      warehouseId: (v) => (v ? null : t("delivery:route.wizard.warehouseRequired")),
    },
  })

  // Step-4 (Driver + Vehicle) state — Phase B
  const currentBusinessId = useSelector(
    (s: { auth: IAuth }) => s.auth.user?.currentBusinessId ?? null,
  )

  // Бүс (Step 1) options come from the network "Борлуулалтын чиглэл /
  // Түгээлтийн бүс" config (de_network.distribution_area), loaded app-wide
  // via /ord/general/init and kept in Redux. No static list — these are the
  // operator's own per-business zones.
  //
  // A бүс (parent) bundles its чиглэл (children); a чиглэл already carries its
  // бүс. So we only list the чиглэлүүд (children) here — selecting a чиглэл
  // pulls its бүс in with it. Each option is labelled "Бүс · Чиглэл".
  const distributionAreas = useSelector(
    (s: { general: { distributionAreas: IDistributionArea[] } }) => s.general.distributionAreas,
  )
  const zoneOptions = useMemo(() => {
    const byId = new Map(distributionAreas.map((a) => [a.id, a] as const))
    return distributionAreas
      .filter((a) => a.id && a.name && a.parentId) // чиглэл only (children of a бүс)
      .map((a) => {
        const parentName = byId.get(a.parentId as string)?.name
        return {
          value: a.id as string,
          label: parentName ? `${parentName} · ${a.name}` : a.name,
        }
      })
      .sort((x, y) => x.label.localeCompare(y.label, "mn"))
  }, [distributionAreas])

  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [vehicleOptions, setVehicleOptions] = useState<Array<{ value: string; label: string }>>([])
  const [vehiclesLoading, setVehiclesLoading] = useState(false)

  const [driverId, setDriverId] = useState<string | null>(null)
  const [driverOptions, setDriverOptions] = useState<Array<{ value: string; label: string }>>([])
  const [driversLoading, setDriversLoading] = useState(false)
  const [driverQuery, setDriverQuery] = useState("")
  const [debouncedDriverQuery] = useDebouncedValue(driverQuery, 300)

  // Ачилтын (бэлдэх) ажилтан — PULL_SHEET_STAFF role. Assigned post-create via
  // the pull-sheet-staff endpoint, which also auto-creates pull sheets for the
  // route's eligible DNs so the picker sees loading tasks immediately.
  const [pullSheetStaffIds, setPullSheetStaffIds] = useState<string[]>([])
  const [pullSheetStaffOptions, setPullSheetStaffOptions] = useState<Array<{ value: string; label: string }>>([])
  const [pullSheetStaffLoading, setPullSheetStaffLoading] = useState(false)
  const [pullSheetStaffQuery, setPullSheetStaffQuery] = useState("")
  const [debouncedPullSheetStaffQuery] = useDebouncedValue(pullSheetStaffQuery, 300)

  const [autoOptimize, setAutoOptimize] = useState(true)
  // The operator arranged the stops (moved, dragged, sorted or reset). A manual order also switches
  // auto-optimize off: the backend's nearest-stop pass would overwrite it right after create.
  const [sequenceSet, setSequenceSet] = useState(false)
  const markSequenceSet = () => {
    setSequenceSet(true)
    setAutoOptimize(false)
  }

  // Load DRAFT DNs — re-fetches on tenant switch
  useEffect(() => {
    if (!currentBusinessId) {
      setDns([])
      return
    }
    let cancelled = false
    setDnsLoading(true)
    // Normal use shows the DRAFT pool. When arriving from the pending list with
    // preselected DNs (which may be ASSIGNED-but-not-yet-routed), also load
    // ASSIGNED so those carried DNs appear and can be pre-selected as stops.
    const statusFilter =
      preselectedDnIds && preselectedDnIds.length > 0 ? ["DRAFT", "ASSIGNED"] : "DRAFT"
    DeliveryApi.list({
      offset: { page: 1, limit: 200 },
      filter: { deliveryNoteStatus: statusFilter },
      order: ["createdAt", "DESC"],
    })
      .then((data: { rows: IDeliveryNotePlus[] }) => {
        if (cancelled) return
        setDns(data.rows ?? [])
      })
      .catch(() => { if (!cancelled) setDns([]) })
      .finally(() => { if (!cancelled) setDnsLoading(false) })
    return () => { cancelled = true }
  }, [currentBusinessId, preselectedDnIds])

  // Apply the DNs passed from the pending-deliveries list once the DRAFT list
  // has loaded: pre-select them (Step 2) and seed the stop order (Step 3),
  // sorted by priority like a manual toggle. Runs once.
  useEffect(() => {
    if (preselectAppliedRef.current) return
    if (!preselectedDnIds || preselectedDnIds.length === 0) return
    if (dns.length === 0) return
    const present = preselectedDnIds.filter((id) => dns.some((dn) => dn.id === id))
    if (present.length === 0) return
    preselectAppliedRef.current = true
    const byId = new Map(dns.map((dn) => [dn.id, dn] as const))
    const sorted = [...present].sort(
      (a, b) => priorityRank(byId.get(b)?.sla?.priority) - priorityRank(byId.get(a)?.sla?.priority),
    )
    setSelectedIds(present)
    setOrderedIds(sorted)
  }, [dns, preselectedDnIds])

  // Load active vehicles for Step 4 — re-fetches on tenant switch
  useEffect(() => {
    if (!currentBusinessId) {
      setVehicleOptions([])
      return
    }
    let cancelled = false
    setVehiclesLoading(true)
    VehicleApi.list({ filter: { isActive: true }, offset: { limit: 200, skip: 0 } })
      .then((res) => {
        if (cancelled) return
        const rows = res?.rows ?? []
        setVehicleOptions(
          rows.map((v) => {
            const tail = [v.make, v.model].filter(Boolean).join(" ")
            const suffix = tail ? ` · ${tail}` : ""
            return { value: v.id, label: `${v.plateNumber} · ${classLabel(v.vehicleClass)}${suffix}` }
          }),
        )
      })
      .catch(() => { if (!cancelled) setVehicleOptions([]) })
      .finally(() => { if (!cancelled) setVehiclesLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusinessId])

  // Tenant switch mid-wizard: clear cross-business selections so the
  // user doesn't accidentally submit DNs/warehouse/driver/vehicle from
  // the previous business (which would 400 server-side).
  //
  // `form` is intentionally omitted from deps — Mantine's useForm returns a
  // fresh object on every render, so including it triggers an infinite
  // setState loop ("Maximum update depth exceeded" from React).
  useEffect(() => {
    setSelectedIds([])
    setOrderedIds([])
    setVehicleId(null)
    setDriverId(null)
    setDriverQuery("")
    setPullSheetStaffIds([])
    setPullSheetStaffQuery("")
    form.setFieldValue("warehouseId", null)
    warehouseTouchedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusinessId])

  // Who may move the start warehouse away from the deliveries' own warehouse.
  useEffect(() => {
    if (!currentBusinessId) { setWarehouseAccess(null); return }
    let cancelled = false
    DeliveryRouteApi.warehouseAccess()
      .then((res) => { if (!cancelled) setWarehouseAccess(res) })
      .catch(() => { if (!cancelled) setWarehouseAccess({ canChangeAny: false, warehouseIds: [] }) })
    return () => { cancelled = true }
  }, [currentBusinessId])

  // Load warehouses for Step 1
  useEffect(() => {
    if (!currentBusinessId) {
      setWarehouseOptions([])
      setWarehouseById(new Map())
      return
    }
    let cancelled = false
    DeliveryApi.warehouseSelect({ businessId: currentBusinessId, limit: 50 })
      .then((data: unknown) => {
        if (cancelled) return
        const list = (Array.isArray(data) ? data : ((data as { rows?: unknown[] })?.rows ?? [])) as Array<{ id: string; name?: string; refCode?: string; locationLat?: string | number | null; locationLng?: string | number | null }>
        setWarehouseOptions(list.map((w) => ({ value: w.id, label: w.name ?? w.refCode ?? w.id })))
        const map = new Map<string, { lat: number; lng: number; name: string }>()
        for (const w of list) {
          const lat = w.locationLat == null ? NaN : Number(w.locationLat)
          const lng = w.locationLng == null ? NaN : Number(w.locationLng)
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            map.set(w.id, { lat, lng, name: w.name ?? w.refCode ?? w.id })
          }
        }
        setWarehouseById(map)
      })
      .catch(() => { if (!cancelled) { setWarehouseOptions([]); setWarehouseById(new Map()) } })
    return () => { cancelled = true }
  }, [currentBusinessId])

  // Debounced driver search
  useEffect(() => {
    if (!currentBusinessId) {
      setDriverOptions([])
      return
    }
    let cancelled = false
    setDriversLoading(true)
    StaffApi.select({
      businessId: currentBusinessId,
      query: debouncedDriverQuery || null,
      limit: 50,
    })
      .then((rows) => {
        if (cancelled) return
        setDriverOptions(
          rows.map((r) => ({
            value: r.id,
            label: (r.lastName || r.firstName ? renderFullName(r) : "") || r.id,
          })),
        )
      })
      .catch(() => { if (!cancelled) setDriverOptions([]) })
      .finally(() => { if (!cancelled) setDriversLoading(false) })
    return () => { cancelled = true }
  }, [currentBusinessId, debouncedDriverQuery])

  // Debounced pull-sheet (loading) staff search
  useEffect(() => {
    if (!currentBusinessId) {
      setPullSheetStaffOptions([])
      return
    }
    let cancelled = false
    setPullSheetStaffLoading(true)
    PullSheetApi.warehouseStaffSelect({
      query: debouncedPullSheetStaffQuery || "",
      limit: 50,
    })
      .then((data: unknown) => {
        if (cancelled) return
        const list = (Array.isArray(data) ? data : ((data as { rows?: unknown[] })?.rows ?? [])) as Array<{
          id: string; lastName?: string | null; firstName?: string | null; email?: string | null
        }>
        setPullSheetStaffOptions(
          list.map((u) => ({
            value: u.id,
            label: (u.lastName || u.firstName ? renderFullName(u) : "") || u.email || u.id,
          })),
        )
      })
      .catch(() => { if (!cancelled) setPullSheetStaffOptions([]) })
      .finally(() => { if (!cancelled) setPullSheetStaffLoading(false) })
    return () => { cancelled = true }
  }, [currentBusinessId, debouncedPullSheetStaffQuery])

  // Warehouse(s) the manager assigned to the selected deliveries when they were created.
  const sourceWarehouseIds = useMemo(() => {
    const byId = new Map(dns.map((dn) => [dn.id, dn] as const))
    const ids = new Set<string>()
    for (const id of selectedIds) {
      const wh = byId.get(id)?.classification?.sourceWarehouseId
      if (wh) ids.add(wh)
    }
    return Array.from(ids)
  }, [dns, selectedIds])
  const defaultWarehouseId = sourceWarehouseIds.length === 1 ? sourceWarehouseIds[0] : null

  // Start warehouse follows the deliveries until the operator changes it by hand.
  useEffect(() => {
    if (warehouseTouchedRef.current) return
    if (form.values.warehouseId !== defaultWarehouseId) form.setFieldValue("warehouseId", defaultWarehouseId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultWarehouseId])

  // Mirrors start-warehouse.ts: a manager may pick any warehouse; anyone else may leave the
  // deliveries' warehouse only for one they also work in, and only if they work in the source too.
  const canChangeWarehouse = useMemo(() => {
    if (sourceWarehouseIds.length === 0) return true
    if (!warehouseAccess) return false
    if (warehouseAccess.canChangeAny) return true
    const mine = new Set(warehouseAccess.warehouseIds)
    return sourceWarehouseIds.every((id) => mine.has(id))
  }, [sourceWarehouseIds, warehouseAccess])

  const routeWarehouseOptions = useMemo(() => {
    if (sourceWarehouseIds.length === 0 || warehouseAccess?.canChangeAny) return warehouseOptions
    const mine = new Set(warehouseAccess?.warehouseIds ?? [])
    return warehouseOptions.map((o) => {
      const isSource = sourceWarehouseIds.length === 1 && o.value === sourceWarehouseIds[0]
      return isSource || (canChangeWarehouse && mine.has(o.value)) ? o : { ...o, disabled: true }
    })
  }, [warehouseOptions, sourceWarehouseIds, warehouseAccess, canChangeWarehouse])

  const warehouseHint = sourceWarehouseIds.length === 0
    ? null
    : sourceWarehouseIds.length > 1
      ? t("delivery:route.wizard.warehouseMixed")
      : canChangeWarehouse
        ? t("delivery:route.wizard.warehouseFromDelivery")
        : t("delivery:route.wizard.warehouseManagerOnly")

  const handleWarehouseChange = (value: string | null) => {
    warehouseTouchedRef.current = true
    form.setFieldValue("warehouseId", value)
  }

  const startPin = useMemo(() => {
    if (!form.values.warehouseId) return null
    return warehouseById.get(form.values.warehouseId) ?? null
  }, [warehouseById, form.values.warehouseId])

  // Бүс is not stored on the route (contract §2); the selected бүс / чиглэл narrow the order list instead.
  // A delivery note that carries no area (the list does not always hydrate it) stays visible only when
  // none of the loaded notes carries one, so the filter never blanks the list by itself.
  const selectedZones = form.values.zone
  const anyAreaInfo = useMemo(() => dns.some((dn) => dn.region?.id || dn.direction?.id), [dns])

  // Filtered DN list
  const visibleDns = useMemo(() => {
    return dns.filter((dn) => {
      if (selectedZones.length > 0 && anyAreaInfo) {
        const ids = [dn.region?.id, dn.direction?.id].filter(Boolean) as string[]
        if (!ids.some((id) => selectedZones.includes(id))) return false
      }
      const channel = dn.classification?.deliveryChannel
      if (filters.channel && channel && channel !== filters.channel) return false
      if (filters.channel === "LOCAL_DELIVERY" && channel === "SUPPLY_FREIGHT") return false
      if (filters.vipOnly) {
        const p = dn.sla?.priority
        if (p !== "VIP" && p !== "URGENT") return false
      }
      if (filters.slaRisk && !dn.sla?.slaBreached) return false
      if (filters.search) {
        const q = filters.search.toLowerCase()
        const ref = (dn.refCode ?? "").toLowerCase()
        // Company names are matched against both stored spellings, never
        // transliterated -- see renderBusinessName.
        const nameHit =
          matchesBusinessName(dn.receiverBusiness, filters.search) ||
          matchesBusinessName(dn.receiverBusiness?.partner, filters.search)
        if (!ref.includes(q) && !nameHit) return false
      }
      return true
    })
  }, [dns, filters, selectedZones, anyAreaInfo])

  // Selected DNs in current order
  const orderedStops = useMemo(() => {
    const byId = new Map(dns.map((dn) => [dn.id, dn] as const))
    return orderedIds.map((id) => byId.get(id)).filter((x): x is DnRow => !!x)
  }, [dns, orderedIds])

  const totalKm = useMemo(() => totalRouteKm(orderedStops, startPin), [orderedStops, startPin])

  const detectedChannel = useMemo<"LOCAL_DELIVERY" | "MIXED" | null>(() => {
    if (orderedStops.length === 0) return null
    const channels = new Set(orderedStops.map((dn) => dn.classification?.deliveryChannel ?? "UNKNOWN"))
    if (channels.size === 1 && channels.has("LOCAL_DELIVERY")) return "LOCAL_DELIVERY"
    return "MIXED"
  }, [orderedStops])

  const handleToggle = (id: string, next: boolean) => {
    setSelectedIds((prev) => {
      if (next) return prev.includes(id) ? prev : [...prev, id]
      return prev.filter((x) => x !== id)
    })
    // A newly added stop is slotted in by priority; the operator has not placed it yet.
    if (next) setSequenceSet(false)
    setOrderedIds((prev) => {
      if (next) {
        if (prev.includes(id)) return prev
        // Auto-sort newly-added by priority desc, then by order added
        const newList = [...prev, id]
        const byId = new Map(dns.map((dn) => [dn.id, dn] as const))
        return newList.sort((a, b) => priorityRank(byId.get(b)?.sla?.priority) - priorityRank(byId.get(a)?.sla?.priority))
      }
      return prev.filter((x) => x !== id)
    })
  }

  const handleMove = (idx: number, dir: -1 | 1) => {
    markSequenceSet()
    setOrderedIds((prev) => {
      const next = [...prev]
      const target = idx + dir
      if (target < 0 || target >= next.length) return prev
        ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }

  const handleReorder = (fromIdx: number, toIdx: number) => {
    markSequenceSet()
    setOrderedIds((prev) => {
      const next = [...prev]
      const [moved] = next.splice(fromIdx, 1)
      next.splice(toIdx, 0, moved)
      return next
    })
  }

  const handleRemove = (id: string) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id))
    setOrderedIds((prev) => prev.filter((x) => x !== id))
  }

  const handleReset = () => {
    // Reset stop order to selection order, sorted by priority desc.
    const byId = new Map(dns.map((dn) => [dn.id, dn] as const))
    setOrderedIds([...selectedIds].sort((a, b) =>
      priorityRank(byId.get(b)?.sla?.priority) - priorityRank(byId.get(a)?.sla?.priority),
    ))
  }

  const [optimizing, setOptimizing] = useState(false)
  const handleAutoOptimize = async () => {
    // Pre-create: backend optimize requires a persisted route, so this button
    // does a client-side priority sort. Real geometric optimize runs post-create
    // via the autoOptimize checkbox in the action bar.
    setOptimizing(true)
    handleReset()
    markSequenceSet()
    setTimeout(() => setOptimizing(false), 200)
  }

  const zoneCounts = useMemo(() => ({
    current: visibleDns.length,
    total: dns.length,
  }), [visibleDns, dns])

  // Required to create (red asterisks, user 2026-11-05): name, start warehouse, date, ≥1 order, vehicle, driver,
  // at least one loader (бэлдэх ажилтан). Бүс, гарах цаг, тэмдэглэл stay optional.
  const isInfoComplete = !!(form.values.plannedDate && form.values.name?.trim() && form.values.warehouseId)
  const canSubmit = isInfoComplete && selectedIds.length > 0 && !!vehicleId && !!driverId && pullSheetStaffIds.length > 0

  // @dehub/help: vars pick the drawer's «Одоо» card; the checklist feeds «Маршрут үүсгэх»'s disabled reason.
  const freightCount = orderedStops.filter((dn) => dn.classification?.deliveryChannel === "SUPPLY_FREIGHT").length
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({
      vars: {
        selectedCount: selectedIds.length,
        noSelection: selectedIds.length === 0,
        freightCount,
      },
    })
  }, [selectedIds.length, freightCount, setHelpContext])
  const checklistItems = useMemo(() => [
    { id: "name", done: !!form.values.name?.trim(), target: "f-name" },
    { id: "warehouse", done: !!form.values.warehouseId, target: "f-warehouse" },
    { id: "date", done: !!form.values.plannedDate, target: "f-date" },
    { id: "pick", done: selectedIds.length > 0, target: "sec-pick" },
    { id: "vehicle", done: !!vehicleId, target: "f-vehicle" },
    { id: "driver", done: !!driverId, target: "f-driver" },
    { id: "loader", done: pullSheetStaffIds.length > 0, target: "f-loader" },
  ], [form.values.name, form.values.warehouseId, form.values.plannedDate, selectedIds.length, vehicleId, driverId, pullSheetStaffIds.length])
  const checklistMissing = useChecklistMissing()

  // Step line: a step is done or not, nothing in between. A single stop needs no sequence; several are
  // done once the operator arranged them or auto-optimize (with a start warehouse) will order them.
  const stepDone = (n: 1 | 2 | 3 | 4): boolean => {
    if (n === 1) return isInfoComplete
    if (n === 2) return selectedIds.length > 0
    if (n === 3) {
      if (orderedIds.length === 0) return false
      return orderedIds.length === 1 || sequenceSet || (autoOptimize && !!startPin)
    }
    return !!(vehicleId && driverId && pullSheetStaffIds.length > 0)
  }

  const handleSubmit = async () => {
    const v = form.values
    if (form.validate().hasErrors || !canSubmit || !v.plannedDate) return
    // Snapshot delivery location at create-time so the map markers + stop
    // addresses survive later branch edits and don't depend on a runtime
    // enrichment fallback. Mirrors the data shape that `get.ts` derives
    // via `branch.locationLat / locationLng / formatAddress(branch)`.
    const dnById = new Map(dns.map((dn) => [dn.id, dn] as const))
    const stops = orderedIds.map((id) => {
      const dn = dnById.get(id)
      const branch = dn?.receiverBranch ?? null
      const rawLat = branch?.locationLat
      const rawLng = branch?.locationLng
      const lat = rawLat == null ? NaN : Number(rawLat)
      const lng = rawLng == null ? NaN : Number(rawLng)
      return {
        deliveryNoteId: id,
        address       : branch?.branchAddress ?? null,
        latitude      : Number.isFinite(lat) ? lat : null,
        longitude     : Number.isFinite(lng) ? lng : null,
      }
    })
    let route
    try {
      route = await create({
        plannedDate: v.plannedDate,
        name: v.name?.trim() || null,
        warehouseId: v.warehouseId || null,
        driverId,
        vehicleId,
        plannedStartTime: plannedStartIso(v.plannedDate, v.startTime),
        notes: v.notes?.trim() || null,
        stops,
      })
    } catch (e) {
      message.error(translateRouteError(e, "error.route.create_failed"))
      return
    }
    // The route exists from here on, so a failed follow-up step must not look like success (O118):
    // each one is reported by name and the operator lands on the route to finish it.
    const warnings: string[] = []
    // Assign the loading (pull-sheet) staff after create — a separate endpoint that also
    // auto-creates pull sheets for the route's eligible DNs.
    if (pullSheetStaffIds.length > 0 && route?.id) {
      try {
        await DeliveryRouteApi.pullSheetStaff(route.id, { pullSheetStaffIds })
      } catch (e) {
        warnings.push(`${t("delivery:route.wizard.loaderFailed")} ${translateRouteError(e)}`)
      }
    }
    if (autoOptimize && startPin && route?.id && orderedIds.length > 0) {
      try {
        await optimize(route.id, {
          startFrom: { lat: startPin.lat, lng: startPin.lng },
          returnTo: { lat: startPin.lat, lng: startPin.lng },
        })
      } catch (e) {
        warnings.push(`${t("delivery:route.wizard.optimizeFailed")} ${translateRouteError(e)}`)
      }
    }
    if (warnings.length > 0) {
      message.error(`${t("delivery:route.wizard.createdWithWarnings")} ${warnings.join(" ")}`)
    } else {
      // Past-tense title + what happens next (R8): driver + vehicle are required, so the route starts «Ачиж байна».
      nextStepToast("order.delivery-routes-new.toast.created", {
        fallback: { title: t("delivery:route.wizard.created") },
      })
    }
    navigate(route?.id ? `/delivery-routes/${route.id}` : "/delivery-routes")
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert color="gray" variant="light">{translateRouteError(error)}</Alert>
      )}

      <Box className={classes.layout}>
        {/* LEFT — sections */}
        <Stack gap="md">
          <RouteInfoSection
            form={form}
            routeNumber={makeRouteNumber(form.values.plannedDate)}
            zoneOptions={zoneOptions}
            warehouseOptions={routeWarehouseOptions}
            warehouseHint={warehouseHint}
            onWarehouseChange={handleWarehouseChange}
            detectedChannel={detectedChannel}
            isComplete={isInfoComplete}
          />
          <DnSelectionSection
            rows={visibleDns}
            loading={dnsLoading}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            filters={filters}
            onFiltersChange={setFilters}
            zoneCounts={zoneCounts}
          />
          <StopSequenceSection
            stops={orderedStops}
            onMove={handleMove}
            onReorder={handleReorder}
            onRemove={handleRemove}
            onAutoOptimize={handleAutoOptimize}
            onReset={() => { handleReset(); markSequenceSet() }}
            optimizing={optimizing}
          />
          <AssignmentSection
            vehicleOptions={vehicleOptions}
            vehiclesLoading={vehiclesLoading}
            vehicleId={vehicleId}
            onVehicleChange={setVehicleId}
            driverOptions={driverOptions}
            driversLoading={driversLoading}
            driverId={driverId}
            onDriverChange={setDriverId}
            onDriverSearch={setDriverQuery}
            driverDisabled={!currentBusinessId}
            pullSheetStaffOptions={pullSheetStaffOptions}
            pullSheetStaffLoading={pullSheetStaffLoading}
            pullSheetStaffIds={pullSheetStaffIds}
            onPullSheetStaffChange={setPullSheetStaffIds}
            onPullSheetStaffSearch={setPullSheetStaffQuery}
            isComplete={!!(vehicleId && driverId && pullSheetStaffIds.length > 0)}
          />
        </Stack>

        {/* RIGHT — map + stats + validation */}
        <Stack gap="md" className={classes.side}>
          <WizardMap stops={orderedStops} totalDistanceKm={totalKm} startPin={startPin} />
          <StatsPanel
            stops={orderedStops}
            detectedChannel={detectedChannel}
            plannedDate={form.values.plannedDate}
            totalDistanceKm={totalKm}
          />
        </Stack>
      </Box>

      {/* Sticky action bar (canvas: step status line, then checkbox left / buttons right) */}
      <Box className={classes.actionBar}>
        <Group gap={22} wrap="wrap">
          {([1, 2, 3, 4] as const).map((n) => {
            return (
              <StepMark key={n} done={stepDone(n)}>
                {`${n}. ${n === 2 ? t("delivery:route.wizard.step2", { count: selectedIds.length }) : t(`delivery:route.wizard.step${n}`)}`}
              </StepMark>
            )
          })}
        </Group>
        <Box className={classes.actionRow}>
          <Group gap={4} wrap="nowrap" className={classes.actionCheck}>
            <Checkbox
              color="dark"
              label={t("delivery:route.wizard.autoOptimize")}
              checked={autoOptimize}
              onChange={(e) => setAutoOptimize(e.currentTarget.checked)}
              disabled={!startPin}
            />
            <HelpIcon helpKey="order.delivery-routes-new.field.auto-optimize" />
          </Group>
          <FormChecklist helpKey="order.delivery-routes-new.checklist.main" items={checklistItems} />
          <Button variant="default" leftSection={<XIcon size={16} />} onClick={() => navigate("/delivery-routes")}>
            {t("delivery:route.wizard.cancel")}
          </Button>
          <DisabledReason helpKey="order.delivery-routes-new.reason.save-draft" reason="SOON" when>
            <Button variant="default">{t("delivery:route.wizard.saveDraft")}</Button>
          </DisabledReason>
          <DisabledReason helpKey="order.delivery-routes-new.reason.create" when={!canSubmit} missing={checklistMissing}>
            <HelpButtonGroup helpKey="order.delivery-routes-new.action.create">
              <Button data-help-id="action-create" leftSection={<CheckCircleIcon size={16} />} onClick={handleSubmit} loading={submitting} disabled={!canSubmit}>
                {t("delivery:route.wizard.create")}
              </Button>
            </HelpButtonGroup>
          </DisabledReason>
        </Box>
      </Box>
    </Stack>
  )
}
